import { Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EventsDataService } from '@services/events-data.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { RulesDataService } from '@services/rules-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';

/**
 * Default Class
 */
@Component({
	selector: 'app-action-list',
	templateUrl: './action-list.component.html',
	styleUrls: ['./action-list.component.scss']
})
export class ActionListComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** actions */
	public actions: Action[] = [];

	/** filteredActions */
	public filteredActions: Action[] = [];

	/** fields */
	public fields: { text: string, value: string } = { text: "ActionType", value: "ActionID" };

	/** actionTypes - values correspond to value in db table */
	public actionTypes:IActionType[]=[]

	/** titleText */
	public titleText: string = "Outstanding Actions";

	/** useTable */
	public useTable: boolean = true;

	/** datasource */
	dataSource: MatTableDataSource<Action>;

	/** Table paginator */
	@ViewChild('paginator') paginator: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sort: MatSort;

	/** displayedColumns */
	displayedColumns: string[] = ['asset', 'action type', 'assigned to', 'target completion', 'created by', 'date created'];

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** now */
	public now: Date = new Date();

	/** myActionsOnly */
	public myActionsOnly: boolean = false;

	/** currentUser */
	public currentUser: WebAppUser;

	/** overDueOnly */
	public overDueOnly: boolean = false;

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe( distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe( distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe( distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe( distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe( distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, currentUser, actionTypes]) =>
				!!selectedAccountGUID && !!hierarchicalData && !!selectedOrganisationUID && !!currentUser && !!actionTypes)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, currentUser, actionTypes]) => {
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;
				this.actionTypes=actionTypes;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.hierarchy = JSON.parse(hierarchicalData);

				this.currentUser = currentUser;

				this.getActions();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getActions
	 */
	getActions() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.actionsDataService.getActions$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((actions) => !!actions),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (actions: any) => {
				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				let listAssetIDs: number[] = [];
				list.forEach(asset => {
					listAssetIDs.push(asset.AssetID);
				})
				this.actions = actions.filter((x: Action) => listAssetIDs.includes(x.AssetID));
				this.filteredActions = this.actions.filter((x: Action) => x.Completed == false);
				this.filterMyActions();
				// this.assignTable();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error);
				} else {

				}
			}
		});
	}

	/**
	 * assignTable
	 */
	assignTable() {
		this.dataSource = new MatTableDataSource(this.filteredActions);
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'asset': return item.AssetName || ''; // Ensure no null/undefined values
				case 'action type': return item.ActionType || ''; // Use AssetName for the 'asset' column
				case 'date created': return item.Timestamp || '';
				case 'target completion': return item.TargetCompletionTimestamp || '';
				default: return '';
			}
		};
	}

	/**
	 * getActionTypeFromValue
	 * @param value value 
	 * @returns 
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * convertEpochNoTime
	 * @param epoch epoch 
	 */
	convertEpochNoTime(epoch: number): string {
		let arr = convertEpochToDateString(epoch).split(" ");
		arr.pop();
		let result: string = "";
		arr.forEach(item => {
			result = result.concat(item, " ");
		})
		return result;
	}

	/**
	 * goToActionDetails
	 * @param action action 
	 */
	goToActionDetails(action: Action) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/actions/${action.ActionID}`]);
	}

	/**
	 * toggleCompleted
	 * @param event event 
	 */
	toggleCompleted(event: any) {
		if (event.checked) {
			this.titleText = "Completed Actions";
			this.displayedColumns.push("date completed");
			this.sort.active = 'date completed';
			this.sort.direction = 'desc';
		} else {
			this.titleText = "Outstanding Actions";
			this.displayedColumns.pop();
			this.sort.active = 'target completion';
			this.sort.direction = 'desc';
		}
		this.filterMyActions();
	}

	/**
	 * toggleOverdue
	 * @param event event 
	 */
	toggleOverdue(event: any) {
		this.overDueOnly = event.checked;
		this.filterMyActions();
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFilteringActions(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		let uncompleted: boolean = this.titleText.includes("Outstanding");
		this.filteredActions = this.actions.filter(data => {
			const isMatchingCompletionStatus = uncompleted ? data.Completed === false : data.Completed !== false;

			if (!isMatchingCompletionStatus) {
				return false;
			}

			if (this.myActionsOnly && data.AssignedToUserSub != this.currentUser.Sub) {
				return false;
			}

			const dateStr = `${convertEpochToDateString(data.Timestamp)}`.toLowerCase();
			const AssetName = `${data.AssetName}`.toLowerCase() || '';
			const ActionType = `${this.getActionTypeFromValue(data.ActionType) || ''}`.toLowerCase();
			const WorkOrderRef = `${data.WorkOrderReference}`.toLowerCase() || '';
			let User = '';
			if (data.AssignedToUserSub) {
				// User = `${this.getUserNameFromSub(data.AssignedToUserSub)}`.toLowerCase() || '';
			}

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${AssetName} ${ActionType} ${WorkOrderRef} ${User}`;
			return combinedStr.includes(query);
		});
		this.dataSource = new MatTableDataSource(this.filteredActions);
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * checkIfOverdue
	 * @param action action 
	 * @returns 
	 */
	checkIfOverdue(action: Action): boolean {
		let result: boolean = false;
		if (action.TargetCompletionTimestamp != undefined && (action.TargetCompletionTimestamp + (1 * 24 * 60 * 60 * 1000)) < this.now.getTime()) {
			if (!action.Completed) {
				result = true;
			}
		}
		return result;
	}

	/**
	 * toggleMyActionsOnly
	 * @param event event 
	 */
	toggleMyActionsOnly(event: any) {
		this.myActionsOnly = event.checked;
		this.filterMyActions();
	}

	/**
	 * filterMyActions
	 */
	filterMyActions() {
		this.filteredActions = this.actions;
		if (this.myActionsOnly) {
			this.filteredActions = this.filteredActions.filter((x: Action) => x.AssignedToUserSub == this.currentUser.Sub);
		}
		if (this.titleText.includes("Outstanding")) {
			this.filteredActions = this.filteredActions.filter((x: Action) => x.Completed == false);
			if (this.overDueOnly) {
				this.filteredActions = this.filteredActions.filter((x: Action) => this.checkIfOverdue(x));
			}
		} else {
			this.filteredActions = this.filteredActions.filter((x: Action) => x.Completed == true);
		}
		this.assignTable();
	}
}
